/**
 * Schema Icon Choices
 *
 * How a select or radio schema field is drawn when the schema builder's "With icons" switch
 * is on (`field.icons`):
 *
 *   select → IconSelect: a button showing the chosen option's icon, label and code, opening
 *            a menu with a search box and the options — the Popular ones first, then the
 *            rest, both in the order set in the builder.
 *   radio  → buildIconPills(): one button per option with its icon and tag, plus a count
 *            and a hint line when the field depends on an icon select (coin → network).
 *
 * The native <select> / radios stay the field's value, so SchemaRenderer reads, validates
 * and narrows these fields exactly as it does the plain ones. Option texts are always set
 * with textContent — they are typed in wp-admin.
 *
 * The picker's own texts come from the page (readChoiceTexts): the wallet twig prints them
 * through __(), so Weglot translates them along with the rest of the cashier.
 *
 * @package betbeast
 */

/**
 * English texts, used when the page's copy is missing or lost a slot in translation.
 * Mirrors the texts block in account-wallet-deposit.twig / account-wallet-withdraw.twig.
 */
const DEFAULT_TEXTS = {
    'select':             'Select {label}',
    'search-placeholder': 'Search name or code, e.g. DOGE',
    'search-label':       'Search coins',
    'group-popular':      'Popular',
    'group-more':         'More coins',
    'match-one':          '1 match',
    'match-many':         '{n} matches',
    'no-match':           'No match for “{query}”',
    'count-one':          'Only network for {coin}',
    'count-many':         '{n} networks',
    'hint-before':        'Pick the network your wallet or exchange will send {coin} on.',
    'hint-after':         'Send {coin} only on the {network} network. Coins sent on another network can be lost.',
};

/** Where the withdrawal wording differs (the texts block says which flow it belongs to). */
const WITHDRAWAL_TEXTS = {
    'hint-before': 'Pick the network of the wallet you’re withdrawing {coin} to.',
    'hint-after':  'Paste a {coin} address on the {network} network. A withdrawal sent to the wrong network can’t be reversed.',
};

/** Unique ids for ARIA references and radio names — both modals can be on one page. */
let uid = 0;

/**
 * Read the picker texts printed in a modal.
 *
 * Each text is a `[data-text]` element whose variable parts are `[data-slot]` elements, so
 * the sentence is translated whole and the values still land in the right place. A copy is
 * filled and read as plain text; if a slot went missing in translation the English text is
 * used instead, rather than a sentence with a hole in it.
 *
 * @param {HTMLElement|null} root Modal holding `[data-schema-choice-texts]`.
 * @returns {function(string, Object=): string} text( key, values )
 */
export function readChoiceTexts( root ) {
    const block    = root?.querySelector( '[data-schema-choice-texts]' ) ?? null;
    const defaults = 'withdrawal' === block?.getAttribute( 'data-schema-choice-texts' )
        ? { ...DEFAULT_TEXTS, ...WITHDRAWAL_TEXTS }
        : DEFAULT_TEXTS;

    return ( key, values = {} ) => {
        const source = block?.querySelector( `[data-text="${ key }"]` ) ?? null;
        if ( source ) {
            const copy   = source.cloneNode( true );
            const filled = Object.keys( values ).every( name => {
                const slot = copy.querySelector( `[data-slot="${ name }"]` );
                if ( slot ) slot.textContent = String( values[ name ] );
                return !! slot;
            } );
            const text = copy.textContent.replace( /\s+/g, ' ' ).trim();
            if ( filled && text ) return text;
        }

        return ( defaults[ key ] ?? '' ).replace( /\{(\w+)\}/g, ( token, name ) => (
            Object.prototype.hasOwnProperty.call( values, name ) ? String( values[ name ] ) : token
        ) );
    };
}

/**
 * The code shown for an option: the one typed in the builder, else its value
 * (BTC is both; Toncoin's value is "GRAM (TON)", so it is given "TON").
 *
 * @param {Object} opt
 * @returns {string}
 */
export function optionCode( opt ) {
    return String( opt?.code || opt?.value || '' );
}

/**
 * An option's icon: the uploaded image, or a round badge with its first letters when there
 * is none or it fails to load.
 *
 * @param {Object}  opt
 * @param {Object}  [options]
 * @param {boolean} [options.lazy] Defer loading (long lists).
 * @param {string}  [options.badge] Letters for the badge; defaults to the code, then label.
 * @returns {HTMLElement}
 */
export function choiceIcon( opt, { lazy = false, badge = '' } = {} ) {
    const makeBadge = () => {
        const letters = ( badge || opt?.code || opt?.label || opt?.value || '' )
            .replace( /[^0-9a-z]/gi, '' )
            .slice( 0, 3 )
            .toUpperCase();
        const el = element( 'span', 'schema-choice-icon schema-choice-icon--badge', letters || '?' );
        el.setAttribute( 'aria-hidden', 'true' );
        return el;
    };

    if ( ! opt?.icon_url ) return makeBadge();

    const img     = document.createElement( 'img' );
    img.className = 'schema-choice-icon';
    img.alt       = '';
    img.width     = 24;
    img.height    = 24;
    img.decoding  = 'async';
    if ( lazy ) img.loading = 'lazy';
    img.addEventListener( 'error', () => img.replaceWith( makeBadge() ), { once: true } );
    img.src = opt.icon_url;
    return img;
}

/**
 * Searchable icon list over a native select.
 *
 * The select keeps the value: a pick sets it and dispatches a bubbling `change`, which is
 * what the renderer listens for (validation, dependent fields, floating label). `root` is
 * the control to put in the field wrapper; `root._schemaSync()` redraws the button after
 * the value was set from code.
 */
export class IconSelect {

    /**
     * @param {HTMLSelectElement} select Built and filled by the renderer.
     * @param {Object}            field  Schema field; options in builder order.
     * @param {Function}          text   From readChoiceTexts().
     */
    constructor( select, field, text ) {
        this.select    = select;
        this.field     = field;
        this.text      = text;
        this.id        = `schema-icon-select-${ ++uid }`;
        this.options   = ( field.options ?? [] ).map( ( opt, index ) => ( {
            ...opt,
            index,
            shownCode: optionCode( opt ),
            haystack:  normalise( [ opt.label, opt.code, opt.value, opt.sub, opt.search ].filter( Boolean ).join( ' ' ) ),
        } ) );
        this.isOpen      = false;
        this.visible     = [];      // options listed now, in display order
        this.active      = -1;      // index into visible (aria-activedescendant)
        this.activeShown = false;   // highlighted, i.e. reached by keyboard or mouse
        this.optionEls   = new Map();
        this.shown     = null;

        this.placing   = 0;

        this._onDocumentClick = this._onDocumentClick.bind( this );
        this._onViewportMove  = this._onViewportMove.bind( this );

        this.root = this._build();
        this.root._schemaSync = () => this.sync();
        this.sync();
    }

    // -------------------------------------------------------------------------
    // Building
    // -------------------------------------------------------------------------

    _build() {
        const root = element( 'div', 'schema-icon-select' );

        // Kept in the DOM as the field's value, hidden from everyone: the button below is
        // what players and assistive tech use.
        this.select.hidden   = true;
        this.select.tabIndex = -1;
        this.select.setAttribute( 'aria-hidden', 'true' );
        this.select.addEventListener( 'change', () => this.sync() );

        this.trigger      = element( 'button', 'schema-icon-select__trigger' );
        this.trigger.type = 'button';
        this.trigger.setAttribute( 'aria-haspopup', 'listbox' );
        this.trigger.setAttribute( 'aria-expanded', 'false' );
        this.trigger.setAttribute( 'aria-controls', `${ this.id }-menu` );
        // validate() focuses this instead of the hidden select.
        this.trigger.setAttribute( 'data-schema-focus', '' );

        this.iconSlot = element( 'span', 'schema-icon-select__icon' );
        this.labelEl  = element( 'span', 'schema-icon-select__label' );
        this.codeEl   = element( 'span', 'schema-icon-select__code' );
        const chevron = element( 'span', 'schema-icon-select__chevron' );
        chevron.setAttribute( 'aria-hidden', 'true' );
        this.trigger.append( this.iconSlot, this.labelEl, this.codeEl, chevron );

        this.menu        = element( 'div', 'schema-icon-select__menu' );
        this.menu.id     = `${ this.id }-menu`;
        this.menu.hidden = true;

        // No placeholder attribute: FloatingLabel claims every input that has one, and would
        // then hide the field's label whenever the search box is left empty. The hint is
        // drawn over the box instead.
        const search     = element( 'div', 'schema-icon-select__search' );
        const searchIcon = element( 'span', 'schema-icon-select__search-icon' );
        searchIcon.setAttribute( 'aria-hidden', 'true' );
        this.input      = document.createElement( 'input' );
        this.input.type = 'search';
        this.input.className = 'schema-icon-select__search-input';
        this.input.setAttribute( 'role', 'combobox' );
        this.input.setAttribute( 'aria-autocomplete', 'list' );
        this.input.setAttribute( 'aria-expanded', 'true' );
        this.input.setAttribute( 'aria-controls', `${ this.id }-list` );
        this.input.setAttribute( 'aria-label', this.text( 'search-label' ) );
        this.input.setAttribute( 'autocomplete', 'off' );
        this.input.setAttribute( 'spellcheck', 'false' );
        this.input.setAttribute( 'enterkeyhint', 'search' );
        this.hint = element( 'span', 'schema-icon-select__search-hint', this.text( 'search-placeholder' ) );
        this.hint.setAttribute( 'aria-hidden', 'true' );
        search.append( searchIcon, this.input, this.hint );
        this.searchRow = search;

        this.list    = element( 'div', 'schema-icon-select__list' );
        this.list.id = `${ this.id }-list`;
        this.list.setAttribute( 'role', 'listbox' );
        this.list.setAttribute( 'aria-label', this.field.label || '' );

        this.empty        = element( 'p', 'schema-icon-select__empty' );
        this.empty.hidden = true;
        this.empty.setAttribute( 'role', 'status' );

        this.menu.append( search, this.list, this.empty );
        root.append( this.select, this.trigger, this.menu );

        this._listen( root );
        return root;
    }

    /**
     * One option row, built once (on first open) and reused while filtering.
     *
     * @param {Object} opt
     * @returns {HTMLElement}
     */
    _optionEl( opt ) {
        if ( this.optionEls.has( opt.index ) ) return this.optionEls.get( opt.index );

        const node = element( 'div', 'schema-icon-select__option' );
        node.id = `${ this.id }-opt-${ opt.index }`;
        node.setAttribute( 'role', 'option' );
        node.dataset.index = String( opt.index );

        const text = element( 'span', 'schema-icon-select__text' );
        text.appendChild( element( 'span', 'schema-icon-select__name', opt.label ) );
        if ( opt.sub ) text.appendChild( element( 'span', 'schema-icon-select__sub', opt.sub ) );

        const check = element( 'span', 'schema-icon-select__check' );
        check.setAttribute( 'aria-hidden', 'true' );

        node.append(
            choiceIcon( opt, { lazy: true, badge: opt.shownCode } ),
            text,
            element( 'span', 'schema-icon-select__code', opt.shownCode ),
            check
        );
        this.optionEls.set( opt.index, node );
        return node;
    }

    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------

    /** The option the native select holds, or null. */
    _selected() {
        return this.options.find( opt => opt.value === this.select.value ) ?? null;
    }

    /** Redraw the button from the native select's value. */
    sync() {
        const opt = this._selected();

        if ( this.shown !== ( opt ? opt.value : '' ) ) {
            this.shown = opt ? opt.value : '';
            this.iconSlot.replaceChildren(
                opt ? choiceIcon( opt, { badge: opt.shownCode } ) : element( 'span', 'schema-choice-icon schema-choice-icon--empty' )
            );
        }

        const placeholder = this.text( 'select', { label: this.field.label } );
        this.labelEl.textContent = opt ? opt.label : placeholder;
        this.codeEl.textContent  = opt ? opt.shownCode : '';
        this.codeEl.hidden       = ! opt;
        this.trigger.setAttribute( 'aria-label', opt ? `${ this.field.label }: ${ opt.label } (${ opt.shownCode })` : placeholder );
        this.root?.classList.toggle( 'has-value', !! opt );
        this._syncFloatingLabel();
    }

    /** The field's floating label shows while a value is set or the list is open. */
    _syncFloatingLabel() {
        // Looked up each time: FloatingLabel adds it to the wrapper after this is built.
        const label = this.root?.closest( '.field' )?.querySelector( '.field__floating-label' );
        label?.classList.toggle( 'is-visible', !! this.select.value || this.isOpen );
    }

    /**
     * List the options for the current search: with none, Popular first and then the rest
     * (headed only when there are both), in builder order; with one, every match, best
     * first — the code itself, then codes and names starting with it, then the rest.
     */
    _renderList() {
        const query = normalise( this.input.value );
        let groups;

        if ( query ) {
            const hits = this.options
                .map( opt => ( { opt, rank: rankOf( opt, query ) } ) )
                .filter( hit => hit.rank < 4 )
                .sort( ( a, b ) => a.rank - b.rank || a.opt.index - b.opt.index )
                .map( hit => hit.opt );
            const label = hits.length
                ? ( 1 === hits.length ? this.text( 'match-one' ) : this.text( 'match-many', { n: hits.length } ) )
                : '';
            groups = [ { label, options: hits } ];
        } else {
            const popular = this.options.filter( opt => opt.popular );
            const rest    = this.options.filter( opt => ! opt.popular );
            groups = popular.length && rest.length
                ? [
                    { label: this.text( 'group-popular' ), options: popular },
                    { label: this.text( 'group-more' ), options: rest },
                ]
                : [ { label: '', options: this.options } ];
        }

        const nodes = groups.map( ( group, g ) => {
            const options = group.options.map( opt => this._optionEl( opt ) );
            if ( ! group.label || ! options.length ) return options;

            const wrap = element( 'div', 'schema-icon-select__group' );
            const head = element( 'div', 'schema-icon-select__group-header', group.label );
            head.id = `${ this.id }-group-${ g }`;
            head.setAttribute( 'role', 'presentation' );
            wrap.setAttribute( 'role', 'group' );
            wrap.setAttribute( 'aria-labelledby', head.id );
            wrap.append( head, ...options );
            return [ wrap ];
        } ).flat();

        this.list.replaceChildren( ...nodes );
        this.visible = groups.flatMap( group => group.options );

        const value = this.select.value;
        this.visible.forEach( opt => {
            const node     = this._optionEl( opt );
            const selected = opt.value === value;
            node.setAttribute( 'aria-selected', String( selected ) );
            node.classList.toggle( 'is-selected', selected );
        } );

        this.list.hidden  = ! this.visible.length;
        this.empty.hidden = !! this.visible.length;
        if ( ! this.visible.length ) {
            this.empty.textContent = this.text( 'no-match', { query: this.input.value.trim() } );
        }

        // Enter picks the best match while searching; otherwise the current choice.
        const selectedIndex = this.visible.findIndex( opt => opt.value === value );
        this._setActive( query ? 0 : selectedIndex, false );
    }

    /**
     * Point the list's active option (aria-activedescendant) at visible[index].
     *
     * @param {number}  index  -1 for none.
     * @param {boolean} reveal Highlight it and scroll it into the list's view — for the
     *                         keyboard and the mouse, not for the list's own redraws.
     */
    _setActive( index, reveal ) {
        const previous = this.visible[ this.active ];
        if ( previous ) this._optionEl( previous ).classList.remove( 'is-active' );

        this.active      = index >= 0 && index < this.visible.length ? index : -1;
        this.activeShown = false;
        const opt        = this.visible[ this.active ];

        if ( ! opt ) {
            this.input.removeAttribute( 'aria-activedescendant' );
            return;
        }

        const node = this._optionEl( opt );
        this.input.setAttribute( 'aria-activedescendant', node.id );
        if ( ! reveal ) return;

        this.activeShown = true;
        node.classList.add( 'is-active' );
        this._scrollIntoList( node );
    }

    /**
     * Bring an option into the list's view. By hand, within the list only:
     * scrollIntoView() would scroll the modal and the page as well.
     *
     * @param {HTMLElement} node
     */
    _scrollIntoList( node ) {
        const top    = node.offsetTop;
        const bottom = top + node.offsetHeight;
        if ( top < this.list.scrollTop ) {
            this.list.scrollTop = top;
        } else if ( bottom > this.list.scrollTop + this.list.clientHeight ) {
            this.list.scrollTop = bottom - this.list.clientHeight;
        }
    }

    // -------------------------------------------------------------------------
    // Open / close / pick
    // -------------------------------------------------------------------------

    /**
     * @param {boolean} focusSearch Move focus to the search box. Not on touch screens,
     *                              where that would pop the keyboard up over the list.
     */
    open( focusSearch = true ) {
        if ( this.isOpen ) return;
        this.isOpen = true;

        this.input.value = '';
        this.hint.hidden = false;
        this._renderList();

        this.menu.hidden = false;
        this.trigger.setAttribute( 'aria-expanded', 'true' );
        this.root.classList.add( 'is-open' );
        this.root.closest( '.field' )?.classList.add( 'is-menu-open' );
        this._syncFloatingLabel();
        this._place();

        // Listeners only while open, removed on close — the renderer rebuilds its fields on
        // every method switch, and a closed picker should leave nothing behind.
        document.addEventListener( 'click', this._onDocumentClick );
        document.addEventListener( 'scroll', this._onViewportMove, true );
        window.addEventListener( 'resize', this._onViewportMove );

        // A long list opens on the current choice.
        const selected = this.visible[ this.active ];
        if ( selected ) this._scrollIntoList( this._optionEl( selected ) );

        if ( focusSearch ) this.input.focus( { preventScroll: true } );
    }

    /**
     * Lay the open list over the page under the field — or above it, when there is more
     * room there. Fixed rather than absolute: the modal scrolls its content, so an absolute
     * list would be cut off at the modal's edge, or scroll the form away from the field.
     */
    _place() {
        if ( ! this.isOpen ) return;

        const field = this.root.closest( '.field' ) ?? this.root;
        const box   = field.getBoundingClientRect();
        const gap   = 4;
        const edge  = 8;
        const below = window.innerHeight - box.bottom - gap - edge;
        const above = box.top - gap - edge;
        const up    = below < 240 && above > below;

        const style    = this.menu.style;
        style.position = 'fixed';
        style.right    = 'auto';
        style.bottom   = 'auto';
        style.left     = `${ box.left }px`;
        style.width    = `${ box.width }px`;

        // The list's own height cap (stylesheet), cut to the room left beside the search row.
        this.list.style.maxHeight = '';
        const cap  = parseFloat( getComputedStyle( this.list ).maxHeight ) || 380;
        const room = ( up ? above : below ) - this.searchRow.offsetHeight - 2;
        this.list.style.maxHeight = `${ Math.max( 120, Math.min( cap, room ) ) }px`;

        const top = up ? box.top - gap - this.menu.offsetHeight : box.bottom + gap;
        style.top = `${ top }px`;

        // A transformed ancestor (the modal's slide-in) makes `fixed` relative to it
        // instead of the viewport: measure where the list landed and make up the difference.
        const landed = this.menu.getBoundingClientRect();
        if ( Math.abs( landed.top - top ) > 0.5 || Math.abs( landed.left - box.left ) > 0.5 ) {
            style.top  = `${ 2 * top - landed.top }px`;
            style.left = `${ 2 * box.left - landed.left }px`;
        }
    }

    /** Keep the open list on its field while anything scrolls or the window resizes. */
    _onViewportMove( event ) {
        // The list's own scrolling moves nothing.
        if ( event?.target === this.list || this.placing ) return;
        this.placing = requestAnimationFrame( () => {
            this.placing = 0;
            this._place();
        } );
    }

    /**
     * @param {boolean} focusTrigger Return focus to the button (after a pick or Escape).
     */
    close( focusTrigger = false ) {
        if ( ! this.isOpen ) return;
        this.isOpen = false;

        this.menu.hidden = true;
        this.menu.removeAttribute( 'style' );
        this.list.style.maxHeight = '';
        this.trigger.setAttribute( 'aria-expanded', 'false' );
        this.input.removeAttribute( 'aria-activedescendant' );
        this.root.classList.remove( 'is-open' );
        this.root.closest( '.field' )?.classList.remove( 'is-menu-open' );
        this._stopListening();

        this.sync();
        if ( focusTrigger ) this.trigger.focus();
    }

    /**
     * Take an option: set the native select and tell the renderer, then close.
     *
     * @param {Object|undefined} opt
     */
    _pick( opt ) {
        if ( opt && this.select.value !== opt.value ) {
            this.select.value = opt.value;
            this.select.dispatchEvent( new Event( 'change', { bubbles: true } ) );
        }
        this.close( true );
    }

    /** @param {MouseEvent} event */
    _onDocumentClick( event ) {
        // Rebuilt away while open (method switch): just stop listening.
        if ( ! this.root.isConnected ) {
            this._stopListening();
            this.isOpen = false;
            return;
        }
        if ( ! this.root.contains( event.target ) ) this.close( false );
    }

    /** Drop the listeners open() added. */
    _stopListening() {
        document.removeEventListener( 'click', this._onDocumentClick );
        document.removeEventListener( 'scroll', this._onViewportMove, true );
        window.removeEventListener( 'resize', this._onViewportMove );
        if ( this.placing ) cancelAnimationFrame( this.placing );
        this.placing = 0;
    }

    // -------------------------------------------------------------------------
    // Events
    // -------------------------------------------------------------------------

    /** @param {HTMLElement} root */
    _listen( root ) {
        this.trigger.addEventListener( 'click', event => {
            if ( this.isOpen ) {
                this.close( false );
                return;
            }
            // detail 0: activated from the keyboard (Enter / Space).
            this.open( 0 === event.detail || prefersSearchFocus() );
        } );

        this.trigger.addEventListener( 'keydown', event => {
            if ( 'ArrowDown' === event.key || 'ArrowUp' === event.key ) {
                event.preventDefault();
                if ( this.isOpen ) {
                    this.input.focus( { preventScroll: true } );
                } else {
                    this.open( true );
                }
            } else if ( 'Escape' === event.key && this.isOpen ) {
                // The withdrawal modal closes on any Escape that reaches the document.
                event.preventDefault();
                event.stopPropagation();
                this.close( true );
            }
        } );

        // Typing searches; it is not a change of this field. Kept from bubbling, or the
        // renderer would take it for a new coin and reset the network below.
        this.input.addEventListener( 'input', event => {
            event.stopPropagation();
            this.hint.hidden = '' !== this.input.value;
            this._renderList();
        } );
        this.input.addEventListener( 'change', event => event.stopPropagation() );

        this.input.addEventListener( 'keydown', event => {
            switch ( event.key ) {
                case 'ArrowDown':
                case 'ArrowUp': {
                    event.preventDefault();
                    // The first press shows where the list stands (best match, or the
                    // current choice) rather than stepping past it.
                    if ( this.active >= 0 && ! this.activeShown ) {
                        this._setActive( this.active, true );
                        break;
                    }
                    const step  = 'ArrowDown' === event.key ? 1 : -1;
                    const last  = this.visible.length - 1;
                    const start = this.active < 0 ? ( step > 0 ? 0 : last ) : this.active + step;
                    this._setActive( Math.max( 0, Math.min( last, start ) ), true );
                    break;
                }
                case 'Enter':
                    // Never submits anything; picks the active option if there is one.
                    event.preventDefault();
                    if ( this.active >= 0 ) this._pick( this.visible[ this.active ] );
                    break;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    this.close( true );
                    break;
            }
        } );

        // Keep focus (and the phone keyboard) where it is while an option is pressed.
        this.list.addEventListener( 'mousedown', event => event.preventDefault() );

        this.list.addEventListener( 'click', event => {
            const node = event.target.closest( '[role="option"]' );
            if ( node ) this._pick( this.options[ Number( node.dataset.index ) ] );
        } );

        this.list.addEventListener( 'mousemove', event => {
            const node  = event.target.closest( '[role="option"]' );
            const index = node ? this.visible.findIndex( opt => opt.index === Number( node.dataset.index ) ) : -1;
            if ( index >= 0 && index !== this.active ) this._setActive( index, true );
        } );

        // Tabbing out closes the list. A click outside is the document listener's.
        root.addEventListener( 'focusout', event => {
            if ( this.isOpen && event.relatedTarget && ! root.contains( event.relatedTarget ) ) {
                this.close( false );
            }
        } );
    }
}

/**
 * Icon buttons over a radio group.
 *
 * Each option is a real radio inside its label, so arrow keys, Tab and the renderer's
 * radio reading all work unchanged. With `coin` (the field depends on an icon select) the
 * head counts the networks and a hint line names the coin and the chosen network.
 *
 * @param {Object}   field
 * @param {Object}   context
 * @param {Function} context.text    From readChoiceTexts().
 * @param {string}   context.checked Value to check, or ''.
 * @param {?{code: string}} context.coin Chosen option of the parent icon select, or null.
 * @returns {HTMLElement} `_schemaSync()` redraws the hint after a value set from code.
 */
export function buildIconPills( field, { text, checked = '', coin = null } ) {
    const id      = `schema-pills-${ ++uid }`;
    const options = field.options ?? [];
    const root    = element( 'div', 'schema-pills' );
    root.setAttribute( 'data-schema-field', field.code );

    const head  = element( 'div', 'schema-pills__head' );
    const label = element( 'span', 'schema-pills__label', field.label );
    label.id = `${ id }-label`;
    head.appendChild( label );
    if ( coin ) {
        head.appendChild( element(
            'span',
            'schema-pills__count',
            1 === options.length ? text( 'count-one', { coin: coin.code } ) : text( 'count-many', { n: options.length } )
        ) );
    }

    const grid = element( 'div', 'schema-pills__grid' );
    grid.setAttribute( 'role', 'radiogroup' );
    grid.setAttribute( 'aria-labelledby', label.id );
    if ( field.required ) grid.setAttribute( 'aria-required', 'true' );

    options.forEach( opt => {
        const pill  = element( 'label', 'schema-pill' );
        const radio = document.createElement( 'input' );
        radio.type      = 'radio';
        radio.className = 'schema-pill__input';
        // Unique per render: both wallet modals can hold this field, outside any form, and
        // same-named radios anywhere in the document would uncheck each other.
        radio.name      = `schema_field_${ field.code }_${ id }`;
        radio.value     = opt.value;
        radio.checked   = '' !== checked && opt.value === checked;
        if ( field.required ) radio.required = true;

        const box = element( 'span', 'schema-pill__box' );
        box.append( choiceIcon( opt ), element( 'span', 'schema-pill__label', opt.label ) );
        if ( opt.tag ) box.appendChild( element( 'span', 'schema-pill__tag', opt.tag ) );

        pill.append( radio, box );
        grid.appendChild( pill );
    } );

    root.append( head, grid );

    if ( coin ) {
        const hint     = element( 'p', 'schema-pills__hint' );
        const hintIcon = element( 'span', 'schema-pills__hint-icon' );
        const hintText = element( 'span', 'schema-pills__hint-text' );
        hintIcon.setAttribute( 'aria-hidden', 'true' );
        hint.id = `${ id }-hint`;
        hint.append( hintIcon, hintText );
        grid.setAttribute( 'aria-describedby', hint.id );
        root.appendChild( hint );

        const sync = () => {
            const value = grid.querySelector( 'input[type="radio"]:checked' )?.value;
            const opt   = options.find( o => o.value === value );
            hintText.textContent = opt
                ? text( 'hint-after', { coin: coin.code, network: opt.tag ? `${ opt.label } (${ opt.tag })` : opt.label } )
                : text( 'hint-before', { coin: coin.code } );
            hint.classList.toggle( 'is-chosen', !! opt );
        };
        grid.addEventListener( 'change', sync );
        root._schemaSync = sync;
        sync();
    }

    return root;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function element( tag, className, text ) {
    const el     = document.createElement( tag );
    el.className = className;
    if ( undefined !== text ) el.textContent = text;
    return el;
}

/**
 * Lower case, accents and outer spaces removed — "Éther " finds "ether".
 *
 * @param {string} value
 * @returns {string}
 */
function normalise( value ) {
    return String( value ?? '' ).normalize( 'NFD' ).replace( /[̀-ͯ]/g, '' ).toLowerCase().trim();
}

/**
 * How well an option matches a search, best first: 0 its code, 1 a code starting with it,
 * 2 a label starting with it, 3 anywhere in its texts, 4 no match.
 *
 * @param {Object} opt   Option with `shownCode` and `haystack`.
 * @param {string} query Normalised.
 * @returns {number}
 */
function rankOf( opt, query ) {
    const code = normalise( opt.shownCode );
    if ( code === query ) return 0;
    if ( code.startsWith( query ) ) return 1;
    if ( normalise( opt.label ).startsWith( query ) ) return 2;
    return opt.haystack.includes( query ) ? 3 : 4;
}

/** A mouse or trackpad: the search box can take focus straight away. */
function prefersSearchFocus() {
    return window.matchMedia?.( '(hover: hover) and (pointer: fine)' ).matches ?? true;
}
